import type { Strings } from './types'

export const en: Strings = {
  common: {
    back: 'Back',
    continue: 'Continue',
    cancel: 'Cancel',
    close: 'Close',
    retry: 'Retry',
    loading: 'Loading…',
    save: 'Save',
    follow: 'Follow',
    following: 'Following',
    showMenu: 'Show menu',
    appbarBack: 'Back',
    appbarClose: 'Close',
    administration: 'Administration',
    adminPanel: 'Admin Panel',
    adminLite: 'Admin Lite',
  },
  search: {
    loadFailed: 'Could not load search',
    startChatFailed: 'Could not start conversation',
    retry: 'Retry',
    noUsers: 'No users found',
    chatHint: 'Search users to message...',
    startingChat: 'Starting chat',
    message: 'Message',
    clearSearch: 'Clear search',
    filters: 'Filters',
    clear: 'Clear',
    country: 'Country',
    gender: 'Gender',
    applyFilters: 'Apply Filters',
    allOption: 'All',
    opponentId: 'Opponent user ID',
    defaultHint: 'Search users...',
    recentHeader: 'Recent Searches',
  },
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    filterLabel: 'Notifications filter',
    tabAll: 'All',
    tabUnread: 'Unread',
    retry: 'Retry',
    emptyAll: 'No notifications yet',
    emptyUnread: 'No unread notifications',
    justNow: 'Just now',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
    monthsAgo: '{n}mo ago',
    yearsAgo: '{n}y ago',
    loadFailed: 'Could not load notifications.',
    kycUpdated: 'Your identity verification was updated',
    postModerated: 'Your post was moderated by an admin',
    pushTitle: 'Push Notifications',
    pushUnsupported: 'Push notifications not supported',
    pushUnavailable: 'Push notifications unavailable',
    pushInstallHint: 'Install the app to enable push on iOS',
    pushDeniedHint: 'Blocked in the browser settings',
    pushChangeFailed: 'Could not change push settings.',
    pushOn: 'On',
    pushOff: 'Off',
  },
  onboarding: {
    slide1Title: 'Meet people near you',
    slide1Body: 'Discover who is around you in real time',
    slide2Title: 'Go live',
    slide2Body: 'Share your moments with the world and earn diamonds',
    slide3Title: 'Earn and spend diamonds',
    slide3Body: 'Gift, play and unlock exclusive content',
    start: 'Get started',
    skip: 'Skip',
  },
  splash: {
    tagline: 'Connect with exclusive models',
  },
  language: {
    spanish: 'Español',
    english: 'English',
  },
  auth: {
    errors: {
      noConnection: 'No internet connection. Please try again.',
      invalidCredentials: 'Invalid credentials. Please check your email and password.',
      duplicateAccount: 'An account with this email or username already exists.',
      invalidCode: 'Invalid verification code. Please try again.',
    },
    login: {
      title: 'Welcome',
      subtitle: 'Sign in to continue',
      credentialLabel: 'Email, username, or phone',
      passwordLabel: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      forgotPassword: 'Forgot your password?',
      submit: 'Sign In',
      divider: 'or',
      useBiometrics: 'Use biometrics',
      noAccount: "Don't have an account?",
      signUpLink: 'Sign up',
      credentialRequired: 'Enter your email, username, or phone',
      passwordRequired: 'Enter your password',
      passwordTooShort: 'Password must be at least 8 characters',
      biometricNotEnrolled: 'Set up biometrics in your profile first',
      biometricUnavailable: 'Biometrics unavailable on this device',
      biometricFailed: 'Biometric authentication failed',
    },
    register: {
      title: 'Create account',
      subtitle: 'Complete your details to sign up',
      progressLabel: 'Registration progress',
      stepAccount: 'Account',
      stepUsername: 'Username',
      stepPassword: 'Password',
      stepContact: 'Contact',
      chooseAccountType: 'Choose your account type',
      accountClient: 'Client',
      accountClientDescription: 'Watch content and follow your creators',
      accountCreator: 'Creator',
      accountCreatorDescription: 'Post content and earn money',
      accountAgency: 'Agency',
      accountAgencyDescription: 'Manage creators and campaigns',
      kycNote: 'Creator and agency accounts require identity verification (KYC).',
      usernameLabel: 'Username',
      usernamePlaceholder: '@username',
      checkingAvailability: 'Checking availability',
      usernameAvailable: 'Username available',
      availableMessage: 'Available!',
      takenMessage: 'That username is taken, choose another',
      suggestionsHint: 'Try:',
      passwordLabel: 'Password',
      confirmPasswordLabel: 'Confirm password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      strengthPrefix: 'Strength',
      passwordRequired: 'Enter a password',
      passwordTooShort: 'Minimum 8 characters',
      passwordsMismatch: 'Passwords do not match',
      methodLabel: 'Sign-up method',
      methodEmail: 'Email',
      methodPhone: 'Phone',
      genderLabel: 'Gender (optional)',
      genderMale: 'Male',
      genderFemale: 'Female',
      genderUnspecified: 'Unspecified',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      phonePlaceholder: '+54 9 11 1234-5678',
      emailRequired: 'Enter your email',
      emailInvalid: 'Invalid email',
      phoneRequired: 'Enter your phone number',
      submit: 'Create Account',
      terms: 'By signing up you accept the Terms and Conditions',
      successTitle: 'Account created!',
      username: {
        required: 'Enter a username',
        tooShort: 'Minimum 3 characters',
        tooLong: 'Maximum 30 characters',
        invalidChars: 'Only letters, numbers, dots, and underscores',
        edgeChar: 'Cannot start or end with a dot or underscore',
        consecutiveSeparators: 'Do not use consecutive dots or underscores',
      },
      strength: {
        weak: 'Weak',
        fair: 'Fair',
        strong: 'Strong',
        veryStrong: 'Very strong',
      },
    },
    sms: {
      title: 'Verify your number',
      noVerifiedPhone: "You don't have a verified phone yet.",
      addPhoneToContinue: 'Add your phone number to continue.',
      phoneLabel: 'Phone',
      phonePlaceholder: '+54 9 11 1234-5678',
      sendCode: 'Send code',
      codeSentPrefix: 'We sent a code to',
      codeLabel: 'Verification code',
      verify: 'Verify',
      resendCountdownPrefix: 'Resend code in',
      resend: 'Resend code',
      verifiedOk: 'Code verified successfully',
    },
    forgot: {
      requestTitle: 'Recover your password',
      requestSubtitle: 'Enter your email, username, or phone and we will send you a code to create a new one',
      credentialLabel: 'Email, username, or phone',
      sendCode: 'Send code',
      credentialRequired: 'Enter your email, username, or phone',
      confirmTitle: 'Verify the code',
      codeSentPrefix: 'We sent a code to',
      edit: 'Edit',
      codeLabel: 'Verification code',
      codeLengthError: 'Enter the 6-digit code',
      newPasswordLabel: 'New password',
      confirmPasswordLabel: 'Confirm password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      passwordRequired: 'Enter a password',
      passwordTooShort: 'Minimum 8 characters',
      passwordsMismatch: 'Passwords do not match',
      updatePassword: 'Update password',
      resendCountdownPrefix: 'Resend code in',
      resend: 'Resend code',
      doneTitle: 'Password updated',
      doneSubtitle: 'You can now sign in with your new password',
      loginButton: 'Sign in',
      invalidOrExpiredCode: 'The code is invalid or expired. Request a new code and try again.',
    },
    biometric: {
      title: 'Protect your account',
      subtitle: 'Choose how to unlock the app',
      biometricTitle: 'Face ID / Fingerprint',
      biometricSubtitle: 'Use biometrics to sign in faster',
      pinTitle: 'PIN code',
      pinSubtitle: 'A 6-digit PIN as backup',
      skip: 'Skip for now',
      comingSoon: 'Coming soon',
      unsupportedDevice: 'This device does not support biometrics',
      authFailed: 'Biometric authentication failed',
      errorPrefix: 'Error',
    },
  },
  profile: {
    genderLabel: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderUnspecified: 'Unspecified',
    statsPosts: 'Posts',
    statsFollowers: 'Followers',
    statsFollowing: 'Following',
    editTitle: 'Edit Profile',
    save: 'Save',
    saveChanges: 'Save changes',
    changeAvatar: 'Change avatar',
    displayName: 'Display name',
    bio: 'Bio',
    username: 'Username',
    required: 'Required',
    invalidUsername: 'Invalid username',
    usernameAvailable: 'Username available',
    usernameTaken: 'Username already taken',
    usernameCheckFailed: 'Could not check availability',
    usernameFormat: '3–30 chars; letters, numbers, dots and underscores only',
    usernameChanged: 'Username changed. Please log in again.',
    profileUpdated: 'Profile updated',
    avatarUpdated: 'Avatar updated',
    avatarDeleted: 'Avatar deleted',
    galleryTitle: 'Your avatars',
    galleryEmpty: 'No saved avatars',
    selectAvatar: 'Select avatar {index}',
    deleteAvatar: 'Delete avatar {index}',
    deleteAvatarTitle: 'Delete avatar',
    deleteAvatarBody: 'Do you want to delete this photo from your gallery?',
    deleteCancel: 'Cancel',
    deleteConfirm: 'Delete',
    takePhoto: 'Take photo',
    chooseGallery: 'Choose from gallery',
    viewGallery: 'View gallery',
    editProfile: 'Edit Profile',
    share: 'Share',
    wallet: 'Wallet',
    unlockedContent: 'Unlocked Content',
    games: 'Games',
    settings: 'Settings',
    help: 'Help',
    logout: 'Log out',
    shareTitle: 'Share profile',
    shareHeading: 'Share your profile with anyone',
    shareBody: 'Scan the QR or share the link so they can follow you on Lolax Models.',
    shareCopied: 'Link copied to clipboard',
    copyLink: 'Copy link',
    shareButton: 'Share',
    shareInviteTitle: 'Lolax Models — {name}',
    shareInviteText: 'Join me on Lolax Models: {link}',
    moreOptions: 'More options',
    tabPosts: 'Posts',
    tabReels: 'Reels',
    tabEncounters: 'Encounters',
    reelsSoon: 'Reels coming soon',
    encountersSoon: 'Encounters coming soon',
    noPosts: 'No posts yet',
    profileLoadFailed: 'Could not load the profile.',
    profileUnavailable: 'This profile is not available.',
    followFailed: 'Could not complete the action',
    avatarFormatError: 'Invalid format or image too large',
    avatarUploadFailed: 'Could not upload the avatar',
    avatarSetFailed: 'Could not change the avatar',
    avatarDeleteFailed: 'Could not delete the avatar',
    usernameErrFormat: 'Invalid username format',
    usernameErrTaken: 'Username already taken',
    usernameErrRateLimit: 'You can change your username again in 30 days',
    usernameErrSession: 'Session expired. Please log in again.',
    usernameErrFailed: 'Could not change username',
    profileSaveFailed: 'Could not update the profile',
    retry: 'Retry',
    unlockedToast: 'Post unlocked',
    comingSoon: 'Coming soon',
    unlockedTitle: 'Unlocked Content',
    unlockedEmptyTitle: 'No unlocked content yet',
    unlockedEmptyBody: 'Premium posts you unlock will appear here',
    unlockedEnd: 'No more unlocked posts',
  },
  kyc: {
    extendedReviewNotice: 'Your verification needs an extended review: it may take longer',
    chipNone: 'Not verified',
    chipPending: 'In review',
    chipVerified: 'Verified',
    chipRejected: 'Rejected',
    cardNone: 'Not verified',
    cardPending: 'In review',
    cardVerified: 'Verification approved',
    cardRejected: 'Verification rejected',
    actionVerify: 'Verify now',
    actionRetry: 'Retry',
    subNone: 'To publish premium content you need to verify your identity.',
    subPending: 'Submitted {date} · estimated time 24-48h',
    subVerified: 'You can now publish premium content.',
    subRejectedNoReason: 'Your document was not accepted. Try again.',
    subRejectedReason: 'Reason: {reason}',
    rowTitle: 'KYC verification status',
    rowNone: 'Not verified',
    rowPending: 'Document in review',
    rowVerified: 'Identity verified',
    rowRejected: 'Document rejected',
    offline: 'Offline, showing last status',
    uploading: 'Uploading document…',
    loadingStatus: 'Loading verification status',
    staleData: 'Stale data',
    errNotRequired: 'Only Creator and Agency accounts require KYC.',
    errPending: 'You already have a submission in review.',
    errMaxAttempts: 'You have reached the maximum number of attempts. Contact support.',
    errTooLarge: 'The file is too large. Maximum 20MB.',
    errBadFormat: 'Unsupported file type. Use JPG, PNG or PDF.',
    errRequired: 'Attach an identity document before submitting.',
    errFallback: 'We could not submit your document. Try again.',
    loadingLabel: 'Loading…',
    uploadingLabel: 'Uploading…',
    processingLabel: 'Processing…',
    loadFailed: 'We could not get your verification status.',
    retry: 'Retry',
    uploadDocument: 'Upload document',
  },
  wallet: {
    buyDiamonds: 'Buy Diamonds',
    packages: 'Packages',
    popular: 'POPULAR',
    bestValue: 'BEST VALUE',
    bonus: '+{n} bonus',
    recent: 'Recent activity',
    emptyActivity: 'No recent activity',
    selectPackage: 'Choose a package',
    confirm: 'Confirm',
    payTitle: 'Pay',
    packageUnavailable: 'Package not available.',
    back: 'Back',
    methodLabel: 'Payment method',
    methodWompi: 'Card (Wompi)',
    methodCrypto: 'Crypto',
    summaryDiamonds: '{total} Diamonds',
    summaryBonus: 'Includes {n} bonus',
    wompiUnavailable: 'Card payments unavailable.',
    payIntro: 'You pay {price} by card. Charged in Colombian pesos.',
    cardNumber: 'Card number',
    expMonth: 'Expiry month',
    expYear: 'Expiry year',
    monthPlaceholder: 'MM',
    yearPlaceholder: 'YY',
    cvc: 'CVC',
    holder: 'Cardholder',
    installments: 'Installments',
    installmentOne: '{n} installment',
    installmentMany: '{n} installments',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    acceptTermsA: 'I accept the',
    termsLink: 'terms and conditions',
    acceptTermsB: 'of the payment.',
    authDataA: 'I authorize the processing of my personal data',
    authDataLink: '(see authorization)',
    validating: 'Validating your card…',
    processing: 'Processing the payment…',
    confirming: 'Confirming the payment of {amount}…',
    payButton: 'Pay {price}',
    purchaseSuccess: 'Purchase successful',
    paymentDeclined: 'The payment was declined. Check your card details or try another.',
    pendingTitle: 'Payment in progress',
    pendingBody: 'We received your payment and are confirming it. Your diamonds will appear in your wallet in a few minutes.',
    backToWallet: 'Back to wallet',
    cryptoPendingTitle: 'Payment under review',
    cryptoPendingBody: 'We registered your hash. An admin confirms it and your {n} diamonds appear in your wallet.',
    sendExactA: 'Send exactly',
    sendExactB: 'in',
    sendExactC: 'to this address',
    depositAddress: 'Deposit address',
    copied: 'Copied!',
    copyAddress: 'Copy address',
    receiveDiamonds: 'You receive {n} diamonds',
    txHash: 'Transaction hash',
    txPlaceholder: 'Paste your transfer hash',
    sentIt: 'I already sent it',
    selectIntroA: 'You pay',
    selectIntroB: 'in crypto. Choose the currency:',
    viewAddress: 'View payment address',
    errors: {
      loadFailed: 'Could not load wallet.',
      noConnection: 'No internet connection. Please try again.',
      sessionExpired: 'Session expired. Please log in again.',
      insufficient: 'Insufficient diamonds or invalid request.',
      giftFailed: 'Could not send the gift.',
      giftSent: 'Gift sent. Remaining balance: {balance}',
      cryptoIntentFailed: 'We could not create the payment intent.',
      cryptoHashFailed: 'We could not register your hash.',
      wompiAcceptanceFailed: 'We could not load the payment terms.',
      wompiUnavailable: 'Card payments unavailable.',
      wompiNoConnection: 'No connection to the payment processor.',
      wompiCardRejected: 'Your card was declined. Check the details.',
      wompiCardInvalid: 'We could not validate your card.',
      wompiPurchaseFailed: 'We could not start the purchase.',
    },
  },
  games: {
    lobbyTitle: 'Games',
    lobbyLoading: 'Loading games',
    retry: 'Retry',
    comingSoon: 'Coming soon',
    unavailable: 'Temporarily unavailable',
    unavailableBadge: 'Unavailable',
    playNow: 'Play now',
    titleSlots: 'Slots',
    titleBlackjack: 'Blackjack',
    titleDarts: 'Darts',
    tagSlots: 'Spin the neon reels and win diamonds.',
    tagBlackjack: 'Beat the dealer to 21.',
    tagDarts: 'Challenge friends in a classic 501 match.',
    backToGames: 'Back to games',
    balanceAria: 'Balance: {n} diamonds. Open wallet.',
    betLabel: 'Bet',
    betAmount: 'Bet amount',
    betRange: 'Bet must be between {min} and {max}',
    errors: {
      noConnection: 'No internet connection. Please try again.',
      sessionExpired: 'Session expired. Please log in again.',
      notEnough: 'Not enough diamonds',
      completePrevious: 'Complete previous game first',
      loadSlots: 'Could not load slots.',
      spinFailed: 'Could not spin.',
      loadBlackjack: 'Could not load blackjack.',
      loadStats: 'Could not load stats.',
      startFailed: 'Could not start the game.',
      playFailed: 'Could not play the hand.',
      loadDarts: 'Could not load darts.',
    },
    slots: {
      spin: 'SPIN',
      win: 'You won {n}!',
      noWin: 'Good luck next time!',
    },
    blackjack: {
      deal: 'DEAL',
      topUp: 'TOP UP',
      dealer: 'Dealer',
      yourHand: 'Your Hand',
      splitHand: 'Split Hand',
      bet: 'Bet {n}',
      topTier: 'Top tier',
      winsToLevel: '{n} wins → Lv.{level}',
      playAgain: 'PLAY AGAIN',
      rechargeDiamonds: 'TOP UP DIAMONDS',
      noDiamondsNext: 'No diamonds left for the next hand',
      emptyTitle: 'You are out of diamonds',
      emptyBody: 'Top up to get back to the table',
      shortfall: 'You need {n} more diamonds for this bet',
      betReturned: 'Bet returned',
      hit: 'HIT',
      stand: 'STAND',
      double: 'DOUBLE',
      split: 'SPLIT',
      outcomeBlackjack: 'Blackjack!',
      outcomeWin: 'You Win!',
      outcomePush: 'Push',
      outcomeBust: 'Bust',
      outcomeDealerWins: 'Dealer Wins',
    },
    darts: {
      statusLoading: 'Loading...',
      statusReady: 'Ready to play',
      statusMatchmaking: 'Matchmaking',
      statusInvite: 'Invite pending',
      turnFirst: 'Your turn first',
      opponentFirst: 'Opponent goes first',
      yourTurn: 'Your turn - tap the board',
      opponentTurn: "Opponent's turn",
      victory: 'Victory!',
      defeat: 'Defeat',
      error: 'Error',
      findMatch: 'FIND MATCH',
      inviteFriend: 'Invite a friend',
      opponentId: 'Opponent user ID',
      sendInvite: 'SEND INVITE',
      inviteSent: 'Invite sent!',
      matchFound: 'Match found!',
      youThrowFirst: 'You throw first',
      opponentThrowsFirst: 'Opponent throws first',
      you: 'You',
      opponent: 'Opponent',
      youWin: 'You Win!',
      youLose: 'You Lose',
      winner: 'Winner: {id}',
      playAgain: 'PLAY AGAIN',
      cancel: 'Cancel',
      finding: 'Finding opponent...',
      betDiamonds: 'Bet: {n} diamonds',
      waitingOpponent: 'Waiting for opponent...',
      dartsCount: 'Darts: {n}/3',
      throw: 'THROW',
      throw3: 'THROW 3 DARTS',
      retry: 'Retry',
    },
  },
  live: {
    listTitle: 'Live',
    goLive: 'Go live',
    loading: 'Loading live streams',
    loadFailed: 'Could not load live streams.',
    retry: 'Retry',
    empty: 'No live streams right now. Be the first!',
    watch: 'Watch {title}',
    host: 'Host {id}',
    joining: 'Joining stream',
    joinFailed: 'Could not join stream',
    tryAgain: 'Please try again later.',
    backToLive: 'Back to live',
    back: 'Back',
    premium: 'Premium',
    premiumTitle: 'Premium stream',
    premiumBody: 'This is a premium live stream. Unlock it to watch.',
    balance: 'Balance: {n} {unit}',
    diamond: 'diamond',
    diamonds: 'diamonds',
    unlocking: 'Unlocking…',
    unlockFor: 'Unlock for {price}',
    unlockStream: 'Unlock stream',
    unlockFailed: 'Could not unlock the stream.',
    unlockInsufficient: 'Not enough diamonds. Top up your balance to unlock this stream.',
    topUp: 'Top up balance',
    notFound: 'Stream not found',
    notFoundBody: 'This stream may have ended or does not exist.',
    endedTitle: 'Stream ended',
    endedBody: 'This live stream has ended.',
    suspendedBody: 'This stream was suspended. {reason}',
    preparing: 'Preparing stream',
    preparingBody: 'Waiting for LiveKit token...',
    connected: 'Connected',
    connectingChat: 'Connecting chat…',
    chatOffline: 'Chat offline',
    giftFailed: 'Could not send the gift.',
    sendGift: 'Send gift',
    chatPlaceholder: 'Say something...',
    chatSend: 'Send chat message',
    guest: 'Guest',
    someone: 'Someone',
    giftSent: '{name} sent {n} 💎',
    giftOverlaySent: 'sent {n} diamonds',
    cameraFailed: 'Could not access camera.',
    close: 'Close',
    end: 'End',
    cameraOn: 'Turn camera on',
    cameraOff: 'Turn camera off',
    micOn: 'Unmute',
    micOff: 'Mute',
    switchCamera: 'Switch camera',
    sendGiftBtn: 'Send gift',
    giftTitle: 'Send Diamonds',
    giftSuccess: 'Gift sent',
    startTitle: 'Go Live',
    startBody: 'Use the camera Live tab to configure your stream.',
    startCta: 'Configure Stream',
    finishTitle: 'Finish Live Stream',
    finishBody: 'What would you like to do with this stream?',
    optFree: 'Save as free post',
    optPremium: 'Save as premium post',
    optDiscard: 'Discard',
    priceError: 'Price must be between 1 and 1000 diamonds',
    diamondPrice: 'Diamond price',
    cancel: 'Cancel',
    endStream: 'End Stream',
    preTitle: 'Go Live',
    streamTitle: 'Stream title',
    streamPlaceholder: 'What are you streaming today?',
    visibility: 'Visibility',
    visPublic: 'Public',
    visFollowers: 'Followers',
    saveAsPost: 'Save as post',
    saveAsPostBody: 'Create a Live post so followers can watch later',
    tags: 'Tags',
    tagsPlaceholder: 'live, music, q&a',
    kycRequired: 'Premium pricing requires KYC verification',
    goLiveCta: 'Go Live',
    startFailed: 'Could not start the stream.',
  },
  chat: {
    back: 'Back',
    chatOptions: 'Chat options',
    connecting: 'Connecting…',
    attachImage: 'Attach image',
    imageUploadWip: 'Image upload not yet implemented',
    typeMessage: 'Type a message...',
    send: 'Send',
    scrollEnd: 'Go to the end',
    today: 'Today',
    yesterday: 'Yesterday',
    userFallback: 'User',
    noMessages: 'No messages yet',
    typing: 'Typing',
    typingAs: '{name} is typing',
    loadConversations: 'Could not load conversations.',
    loadMessages: 'Could not load messages.',
    convUnavailable: 'This conversation is not available.',
    convStartFailed: 'Could not start the conversation.',
    typingShort: 'typing…',
    online: 'online',
    offline: 'offline',
    liveOffline: 'Live offline: {err}',
    retry: 'Retry',
    listTitle: 'Messages',
    listRetry: 'Retry',
    listEmptyTitle: 'No messages yet',
    listEmptyBody: 'Start a conversation by tapping New Message',
    newMessage: 'New Message',
    notDelivered: 'Not delivered · tap to retry',
    roomEmptyBody: 'Send a message to start the conversation',
  },
  reels: {
    createReel: 'Create reel',
    uploadingReel: 'Uploading reel',
    selectVideo: 'Select video file',
    changeVideo: 'Change video',
    caption: 'Caption',
    audioName: 'Audio name',
    location: 'Location',
    uploadReel: 'Upload Reel',
    createTitle: 'Create Reel',
    uploadDone: 'Reel uploaded successfully!',
    createAnother: 'Create Another',
    uploadFailed: 'Reel upload failed',
    like: 'Like',
    comment: 'Comment',
    share: 'Share',
    sendGift: 'Send gift',
    emptyTitle: 'No reels yet',
    emptyBody: 'Be the first to create one',
    errors: {
      noConnection: 'No internet connection. Please try again.',
      sessionExpired: 'Session expired. Please log in again.',
      insufficient: 'Insufficient diamonds or invalid request.',
      fallback: 'Something went wrong. Please try again.',
    },
  },
  nav: {
    home: 'Home',
    reels: 'Reels',
    chat: 'Chat',
    profile: 'Profile',
    search: 'Search',
    notifications: 'Notifications',
  },
  gifts: {
    title: 'Send Gift',
    sent: 'Sent',
    sending: 'Sending…',
    tryAgain: 'Try again',
    buyDiamonds: 'Buy Diamonds',
    sendGift: 'Send Gift',
    available: 'available',
    decrease: 'Decrease amount',
    increase: 'Increase amount',
  },
  create: {
    newPost: 'New Post',
    post: 'Post',
    caption: 'Caption',
    captionPlaceholder: 'Write a caption...',
    tags: 'Tags',
    tagsPlaceholder: 'travel, summer, beach',
    dismiss: 'Dismiss',
    createFailed: 'Create post failed',
    uploading: 'Uploading media... {n}%',
    submitting: 'Creating post...',
    premiumPost: 'Premium post',
    premiumOn: 'Followers pay diamonds to view this post',
    premiumOff: 'Anyone can view this post',
    diamondPrice: 'Diamond price',
    pricePlaceholder: '1 - 1000',
    priceError: 'Price must be between 1 and 1000 diamonds',
    diamondsSuffix: 'diamonds',
    durationError: 'The video exceeds the allowed duration.',
    tabPhoto: 'Photo',
    tabVideo: 'Video',
    tabLive: 'Live',
    cameraUnavailable: 'The camera is not available.',
    close: 'Close',
    cameraPreview: 'Camera preview',
    captureMode: 'Capture mode',
    photoCapture: 'Photo capture',
    videoCapture: 'Video capture',
    stopRecording: 'Stop recording',
    capturePhoto: 'Capture photo',
    recordVideo: 'Record video',
    mediaPreview: 'Selected media preview',
    mediaTypeError: 'The uploaded file type is not supported.',
    mediaSizeError: 'The uploaded file exceeds the allowed size.',
    mediaUnavailable: 'Media preview unavailable',
  },
  settings: {
    title: 'Settings',
    account: 'ACCOUNT',
    editProfile: 'Edit profile',
    changePassword: 'Change password',
    security: 'SECURITY',
    biometric: 'Face ID / Fingerprint',
    biometricUnavailable: 'Biometrics not available on this device',
    pin: 'Access PIN',
    devices: 'Active devices',
    language: 'LANGUAGE',
    logoutSection: 'LOG OUT',
    logout: 'Log out',
    logoutTitle: 'Log out',
    logoutCancel: 'Cancel',
    logoutConfirm: 'Log out',
    logoutBody: 'Are you sure you want to log out?',
    userFallback: 'User',
    usernameFallback: 'username',
    changePasswordTitle: 'Change password',
    pinTitle: 'Access PIN',
    devicesTitle: 'Active devices',
    comingSoon: 'Coming soon',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordMismatch: 'Passwords do not match',
    passwordMin: 'Minimum 8 characters',
    passwordChanged: 'Password changed. Please log in again.',
    changeCta: 'Change password',
    sessionDevice: 'Device',
    sessionUnknown: 'Unknown device',
    sessionRevoke: 'Sign out',
    sessionRevokeConfirm: 'Sign out this session?',
    sessionCurrentWarn: 'This may be the session you are using: you will be logged out here too.',
    sessionsEmpty: 'No active sessions',
    sessionsCount: '{n} active sessions',
    pinCreate: 'Create PIN',
    pinChange: 'Change PIN',
    pinRemove: 'Remove PIN',
    pinRemoveConfirm: 'Remove the access PIN from this account?',
    pinCurrent: 'Current PIN',
    pinNew: 'New PIN',
    pinCode: 'PIN (4-6 digits)',
    pinInvalid: 'Enter a 4 to 6 digit PIN',
    pinSetOk: 'PIN created',
    pinChangedOk: 'PIN changed',
    pinRemovedOk: 'PIN removed',
    pinWrong: 'Wrong PIN',
    pinSave: 'Save',
    lockTitle: 'Enter your PIN',
    lockBody: 'This account is protected with an access PIN on this device.',
    lockUnlock: 'Unlock',
    lockWrong: 'Wrong PIN. Try again.',
    sessionEnded: 'Session ended. Please log in again.',
    helpTitle: 'Help',
    helpHeading: 'How can we help you?',
    helpContact: 'Contact support',
    helpAccount: 'Account and profile',
    helpAccountBody: 'Edit your photo, display name, bio and country from Settings.',
    helpWallet: 'Wallet and diamonds',
    helpWalletBody: 'Buy diamonds by card from the Wallet section. The conversion is credited instantly.',
    helpGames: 'Games',
    helpGamesBody: 'Slots, blackjack and darts use diamonds as currency. Check the rules before betting.',
    helpPrivacy: 'Privacy and security',
    helpPrivacyBody: 'Enable biometrics and review connected devices from Security.',
    helpReport: 'Report a problem',
    helpReportBody: 'If you find a bug or inappropriate behavior, contact us and we will review it.',
  },
  calls: {
    call: 'Call',
    callFree: 'Call free',
    callRate: 'Call · {rate} 💎/min',
    videoCall: 'Video call',
    videoCallFree: 'Free video call',
    videoCallRate: 'Video call · {rate} 💎/min',
    callsDisabled: 'Not accepting calls',
    confirmTitle: 'Confirm call',
    confirmBody: 'This call costs {rate} 💎 per minute. Continue?',
    confirm: 'Call now',
    incomingTitle: 'Incoming call',
    incomingVideoTitle: 'Incoming video call',
    incomingFree: 'Free',
    incomingRate: '{rate} 💎/min',
    accept: 'Accept',
    reject: 'Decline',
    missed: 'Missed call',
    calling: 'Calling…',
    callingVideo: 'Calling (video)…',
    cameraOn: 'Turn camera on',
    cameraOff: 'Turn camera off',
    settingsTitle: 'Voice calls',
    settingsToggle: 'Receive calls',
    rateLabel: 'Price per minute (💎)',
    rateHint: '0 = free calls. Max 1000.',
    rateInvalid: 'Enter a whole number between 0 and 1000',
    mute: 'Mute',
    unmute: 'Unmute',
    speaker: 'Speaker',
    endCall: 'End call',
    connecting: 'Connecting…',
    cost: 'Cost: {cost} 💎',
    balance: 'Balance: {balance} 💎',
    perMin: '{rate} 💎/min',
    joinFailed: 'Could not join the call',
    ended: 'Call ended',
    declined: 'Declined the call',
    cancelled: 'Call cancelled',
  },
  feed: {
    errors: {
      noConnection: 'No internet connection. Please try again.',
      sessionExpired: 'Session expired. Please log in again.',
      deleteForbidden: 'You can only delete your own posts.',
      postNotFound: 'Post not found. It may already be deleted.',
      moderateForbidden: 'You do not have permission to moderate posts.',
      moderateNotFound: 'Post not found or already moderated.',
    },
    screen: {
      retry: 'Retry',
      emptyTitle: 'No posts in your feed yet',
      emptyBody: 'Tap refresh to load posts. Make sure you are signed in.',
      refresh: 'Refresh',
      createPost: 'Create post',
      unlocked: 'Post unlocked',
      comingSoon: 'Coming soon',
    },
    comments: {
      title: 'Comments',
      cancelReply: 'Cancel reply',
      reply: 'Reply',
      replyingTo: 'Replying to {name}',
      sendFailed: 'Failed to send comment. Please try again.',
      loadFailed: 'Failed to load comments',
      empty: 'No comments yet',
      retry: 'Retry',
      placeholder: 'Add a comment...',
      addLabel: 'Add a comment',
      send: 'Send',
    },
    post: {
      comments: 'Comments',
      share: 'Share',
      shareTitle: '{name} on Lolax Models',
      authorProfile: '{name} profile',
      menuDelete: 'Delete',
      menuSuspend: 'Suspend post',
      menuAdminDelete: 'Delete (admin)',
      deleteTitle: 'Delete this post?',
      deleteBody: 'This post will be permanently removed from your feed.',
      cancel: 'Cancel',
      deleteConfirm: 'Delete',
      suspendTitle: 'Suspend this post?',
      suspendWhy: 'Why are you suspending this post?',
      reason: 'Reason',
      suspendConfirm: 'Suspend',
      adminDeleteTitle: 'Delete this post? (admin)',
      adminDeleteBody:
        'This post will be permanently removed as an admin moderation action, regardless of who authored it.',
    },
    premium: {
      title: 'Premium Content',
      tapToUnlock: 'Tap to unlock',
      unlockAria: 'Unlock premium content for {price}',
      diamond: 'diamond',
      diamonds: 'diamonds',
    },
    unlock: {
      title: 'Unlock post',
      unlockFor: 'Unlock for {price} {unit}',
      unlock: 'Unlock',
      unlocked: 'Unlocked',
      unlocking: 'Unlocking…',
      buyDiamonds: 'Buy Diamonds',
      tryAgain: 'Try again',
    },
    unlockErrors: {
      insufficientDiamonds: 'Not enough diamonds to unlock this post',
      alreadyUnlocked: 'You already unlocked this post',
      postNotFound: 'Post not found',
      accessDenied: 'You cannot unlock this post',
      authRequired: 'Authentication required. Please log in again.',
      unlockFailed: 'Unlock failed. Please try again.',
    },
    expand: {
      showMore: 'Show more',
      showLess: 'Show less',
    },
    like: {
      like: 'Like',
      unlike: 'Unlike',
    },
    media: {
      unauthorized: 'Not authorized',
      unavailable: 'Media unavailable',
      failed: 'Could not load media',
    },
    create: {
      mediaUploadFailed: 'Media upload failed',
      createFailed: 'Create post failed',
      mediaNoId: 'Media upload did not return a media ID',
    },
  },
}
